// confetti.service.ts - Angular 9
import { Injectable, Renderer2, RendererFactory2, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface ConfettiOptions {
  count?: number;
  colors?: string[];
  size?: { min: number; max: number };
  duration?: { min: number; max: number };
  delay?: number;
  shapes?: ('circle' | 'square')[];
}

@Injectable({
  providedIn: 'root'
})
export class ConfettiService {
  private renderer: Renderer2;
  private confettiContainer: HTMLElement | null = null;
  private activeIntervals: any[] = [];

  private defaultColors = [
    '#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', 
    '#54a0ff', '#5f27cd', '#00d2d3', '#ff9f43', 
    '#10ac84', '#ee5a24', '#2ed573', '#a4b0be'
  ];

  private defaultOptions: ConfettiOptions = {
    count: 50,
    colors: this.defaultColors,
    size: { min: 8, max: 15 },
    duration: { min: 2, max: 4 },
    delay: 10,
    shapes: ['circle', 'square']
  };

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.createConfettiContainer();
  }

  private createConfettiContainer(): void {
    if (!this.confettiContainer) {
      this.confettiContainer = this.renderer.createElement('div');
      this.renderer.addClass(this.confettiContainer, 'confetti-container');
      this.renderer.setStyle(this.confettiContainer, 'position', 'fixed');
      this.renderer.setStyle(this.confettiContainer, 'top', '0');
      this.renderer.setStyle(this.confettiContainer, 'left', '0');
      this.renderer.setStyle(this.confettiContainer, 'width', '100%');
      this.renderer.setStyle(this.confettiContainer, 'height', '100%');
      this.renderer.setStyle(this.confettiContainer, 'pointer-events', 'none');
      this.renderer.setStyle(this.confettiContainer, 'z-index', '9999');
      this.renderer.appendChild(this.document.body, this.confettiContainer);
    }
  }

  private createConfettiPiece(x: number, y: number, options: ConfettiOptions): void {
    if (!this.confettiContainer) return;

    const confetti = this.renderer.createElement('div');
    const color = options.colors![Math.floor(Math.random() * options.colors!.length)];
    const size = Math.random() * (options.size!.max - options.size!.min) + options.size!.min;
    const duration = Math.random() * (options.duration!.max - options.duration!.min) + options.duration!.min;
    const shape = options.shapes![Math.floor(Math.random() * options.shapes!.length)];

    // Estilos del confetti
    this.renderer.setStyle(confetti, 'position', 'absolute');
    this.renderer.setStyle(confetti, 'left', `${x}px`);
    this.renderer.setStyle(confetti, 'top', `${y}px`);
    this.renderer.setStyle(confetti, 'width', `${size}px`);
    this.renderer.setStyle(confetti, 'height', `${size}px`);
    this.renderer.setStyle(confetti, 'background-color', color);
    this.renderer.setStyle(confetti, 'border-radius', shape === 'circle' ? '50%' : '0');
    this.renderer.setStyle(confetti, 'animation', `confetti-fall ${duration}s linear forwards`);

    // Añadir al contenedor
    this.renderer.appendChild(this.confettiContainer, confetti);

    // Eliminar después de la animación
    setTimeout(() => {
      if (confetti.parentNode) {
        this.renderer.removeChild(this.confettiContainer, confetti);
      }
    }, duration * 1000);
  }

  /**
   * Dispara confetti básico
   */
  basic(customOptions?: Partial<ConfettiOptions>): void {
    const options = Object.assign({}, this.defaultOptions, customOptions);
    this.triggerConfetti(options);
  }

  /**
   * Dispara confetti explosivo (más partículas)
   */
  explosive(customOptions?: Partial<ConfettiOptions>): void {
    const options = Object.assign({}, this.defaultOptions, { 
      count: 300, 
      delay: 20
    }, customOptions);
    this.triggerConfetti(options);
  }

  /**
   * Dispara confetti colorido con más variedad
   */
  colorful(customOptions?: Partial<ConfettiOptions>): void {
    const rainbowColors = [
      '#ff0000', '#ff8000', '#ffff00', '#80ff00', '#00ff00',
      '#00ff80', '#00ffff', '#0080ff', '#0000ff', '#8000ff',
      '#ff00ff', '#ff0080'
    ];
    const options = Object.assign({}, this.defaultOptions, { 
      colors: rainbowColors,
      count: 80
    }, customOptions);
    this.triggerConfetti(options);
  }

  /**
   * Confetti continuo por un período de tiempo
   */
  continuous(durationMs: number = 3000, customOptions?: Partial<ConfettiOptions>): void {
    const options = Object.assign({}, this.defaultOptions, { count: 1 }, customOptions);
    const interval = setInterval(() => {
      const x = Math.random() * window.innerWidth;
      this.createConfettiPiece(x, -10, options);
    }, 100);

    this.activeIntervals.push(interval);

    setTimeout(() => {
      clearInterval(interval);
      this.activeIntervals = this.activeIntervals.filter(i => i !== interval);
    }, durationMs);
  }

  /**
   * Confetti desde un elemento específico
   */
  fromElement(element: HTMLElement, customOptions?: Partial<ConfettiOptions>): void {
    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const options = Object.assign({}, this.defaultOptions, customOptions);
    
    for (let i = 0; i < options.count!; i++) {
      setTimeout(() => {
        const offsetX = (Math.random() - 0.5) * 200;
        const x = centerX + offsetX;
        this.createConfettiPiece(x, centerY, options);
      }, i * (options.delay || 20));
    }
  }

  /**
   * Método principal para disparar confetti
   */
  // private triggerConfetti(options: ConfettiOptions): void {
  //   for (let i = 0; i < options.count!; i++) {
  //     setTimeout(() => {
  //       const x = Math.random() * window.innerWidth;
  //       this.createConfettiPiece(x, -10, options);
  //     }, i * options.delay!);
  //   }
  // }

  private triggerConfetti(options: ConfettiOptions): void {
    // Ejecutar la primera pieza inmediatamente
    const x = Math.random() * window.innerWidth;
    this.createConfettiPiece(x, -10, options);
  
    // Luego el resto con delay
    for (let i = 1; i < options.count!; i++) {
      setTimeout(() => {
        const x = Math.random() * window.innerWidth;
        this.createConfettiPiece(x, -10, options);
      }, i * options.delay!);
    }
  }
  

  /**
   * Limpia todos los intervalos activos
   */
  clearAll(): void {
    this.activeIntervals.forEach(interval => clearInterval(interval));
    this.activeIntervals = [];
  }

  /**
   * Destruye el servicio y limpia recursos
   */
  destroy(): void {
    this.clearAll();
    if (this.confettiContainer && this.confettiContainer.parentNode) {
      this.renderer.removeChild(this.document.body, this.confettiContainer);
      this.confettiContainer = null;
    }
  }
}